/**
 * PropiedadesColumnSelector — panel de selección de columnas visibles
 * para la tabla principal de propiedades. Extraído de propiedades.tsx
 * en round 2026-04-27 (Fase 8).
 *
 * QUÉ HACE
 *   1. Render de un panel con checkbox "Mostrar todo" (master) +
 *      checkboxes individuales por columna + botones "Reset
 *      (importantes)" y "Ver todas".
 *   2. La columna "acciones" SIEMPRE queda visible (no se puede
 *      desactivar) — el admin necesita poder editar/eliminar.
 *
 * POR QUÉ ES UN COMPONENTE LOCAL Y NO COMPARTIDO
 *   El patrón "selector de columnas" se repite también en aseos.tsx
 *   (con `Checkbox.Group`) e inventario.tsx (con `Settings` dropdown).
 *   Los 3 sitios usan APIs ligeramente distintas y comportamientos
 *   diferentes (qué columna queda anclada, dónde guardan visibilidad,
 *   reset semántico). Un `<ConfigurableTable>` global requeriría
 *   homogeneizar las 3 implementaciones primero — lo postergamos.
 *
 *   Por ahora extraemos solo este panel para reducir el tamaño de
 *   propiedades.tsx; cuando llegue el momento de unificar,
 *   este componente sirve de "candidato a generalizar".
 *
 * PROPS
 *   El componente es agnóstico al shape de las columnas — recibe la
 *   lista de keys + el mapa de labels. Eso lo hace levemente
 *   reutilizable: si en el futuro otro Tab de propiedades necesita
 *   un selector análogo (p.ej. otra tabla en bodegas/bodeguitas),
 *   podemos pasarle su propio `keys`/`labels`.
 */

import React from "react";
import { Button, Checkbox, Divider, Space, Typography } from "antd";

const { Text } = Typography;

interface PropiedadesColumnSelectorProps<K extends string> {
  /** Mapa key → label legible (mismo shape que `COL_LABELS`). */
  labels: Record<K, string>;
  /** Estado actual de columnas visibles (subset de keys de `labels`). */
  visible: K[];
  /** Handler para actualizar el estado. El padre owna el state. */
  onChange: (next: K[]) => void;
  /** Lista de keys "default" (las que cuentan como "importantes" en
   *  el botón Reset). El padre la calcula según su criterio (en
   *  propiedades.tsx viene del hook `usePropiedadesLogic`). */
  defaultVisible: K[];
  /** Key que NUNCA se desactiva (típicamente "acciones"). Si se
   *  pasa, se omite del listado de checkboxes y se anexa siempre al
   *  array onChange. */
  alwaysOn?: K;
}

function PropiedadesColumnSelector<K extends string>({
  labels,
  visible,
  onChange,
  defaultVisible,
  alwaysOn,
}: PropiedadesColumnSelectorProps<K>): React.ReactElement {
  const keys = Object.keys(labels) as K[];
  // Sacamos la columna anclada del listado del selector (siempre va
  // al final del array onChange). Si no se pasó alwaysOn, se incluyen
  // todas en el listado.
  const keysSorted = alwaysOn ? keys.filter((k) => k !== alwaysOn) : keys;

  /** Helper que aplica `alwaysOn` al final del array si está definido.
   *  Los Sets internos NO mutan; siempre construimos un nuevo array
   *  ordenado coherente con la convención del padre. */
  const withAlwaysOn = (next: K[]): K[] =>
    alwaysOn && !next.includes(alwaysOn) ? [...next, alwaysOn] : next;

  return (
    <div
      style={{
        padding: 12,
        maxWidth: 320,
        background: "#fff",
        borderRadius: 8,
        // Sombra de AntD Dropdown — mantiene la consistencia visual
        // con otros dropdowns del sistema.
        boxShadow:
          "0 6px 16px 0 rgba(0,0,0,.08), 0 3px 6px -4px rgba(0,0,0,.12), 0 9px 28px 8px rgba(0,0,0,.05)",
      }}
    >
      <Text strong>Columnas</Text>
      <Divider style={{ margin: "10px 0" }} />

      {/*
       * Master checkbox "Mostrar todo":
       *  - checked: si visible incluye AL MENOS la cantidad de defaults.
       *  - indeterminate: si hay alguna columna seleccionada pero no
       *    todas las del catálogo (estado mixto).
       *  - onClick check: agrega todas las keys + alwaysOn.
       *  - onClick uncheck: deja SOLO alwaysOn (no permite tabla
       *    completamente vacía).
       */}
      <Checkbox
        checked={visible.length >= defaultVisible.length}
        indeterminate={
          visible.length > 0 && visible.length < keysSorted.length + 1
        }
        onChange={(e) => {
          if (e.target.checked) {
            onChange(withAlwaysOn(keysSorted));
          } else {
            onChange(withAlwaysOn([]));
          }
        }}
      >
        Mostrar todo
      </Checkbox>

      <Divider style={{ margin: "10px 0" }} />

      {/* Listado scrollable de checkboxes individuales — maxHeight
          evita que en una tabla con 30+ columnas el dropdown crezca
          fuera de la pantalla. */}
      <div style={{ maxHeight: 260, overflow: "auto", paddingRight: 8 }}>
        {keysSorted.map((k) => (
          <div key={k} style={{ marginBottom: 6 }}>
            <Checkbox
              checked={visible.includes(k)}
              onChange={(e) => {
                const next = new Set(visible);
                if (e.target.checked) next.add(k);
                else next.delete(k);
                onChange(withAlwaysOn(Array.from(next) as K[]));
              }}
            >
              {labels[k]}
            </Checkbox>
          </div>
        ))}
      </div>

      <Divider style={{ margin: "10px 0" }} />

      <Space>
        <Button size="small" onClick={() => onChange(defaultVisible)}>
          Reset (importantes)
        </Button>
        <Button
          size="small"
          onClick={() => onChange(withAlwaysOn(keysSorted))}
        >
          Ver todas
        </Button>
      </Space>
    </div>
  );
}

export default PropiedadesColumnSelector;
